import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { usePaymentsManagementQuery } from '@/api/query/payment';
import { usePaymentContext } from '@/contexts/PaymentContext';
import { PaymentManagementAPIResponseMapper } from '@/api/mapper/payment';
import type { Member } from '@/types';
import {
  Search,
  Download,
  Users,
  CheckCircle2,
  AlertCircle,
  IndianRupee,
} from 'lucide-react';
import { PaymentTable } from './PaymentTable';
import { ViewPayment } from './ViewPayment';
import { UpsertPayment } from './UpsertPayment';
import { createColumns, type ColumnSort, type SortField } from './TableColumns';
import {
  formatCurrency,
  getCurrentMonthLabel,
  type PaymentMemberRow,
} from '@/lib/payments';
import { getStatusLabel } from '@/components/StatusBadge';
import {
  getMembershipStatus,
  getPaymentMonthStatus,
  isAddPaymentDisabled,
} from '@/lib/utils';
import { useAppContext } from '@/contexts/AppContext';

type StatusFilter = 'all' | 'paid' | 'unpaid';

function SummaryCard({
  icon: Icon,
  label,
  value,
  hint,
  tone,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  hint?: string;
  tone: 'brand' | 'emerald' | 'amber' | 'gray';
}) {
  const toneStyles = {
    brand: 'border-[#4369a0]/15 bg-[#4369a0]/5',
    emerald: 'border-emerald-100 bg-emerald-50/60',
    amber: 'border-amber-100 bg-amber-50/60',
    gray: 'border-gray-200 bg-white',
  }[tone];

  const iconStyles = {
    brand: 'bg-[#4369a0]/10 text-[#4369a0]',
    emerald: 'bg-emerald-100 text-emerald-700',
    amber: 'bg-amber-100 text-amber-700',
    gray: 'bg-gray-100 text-gray-600',
  }[tone];

  return (
    <div className={`rounded-xl border p-4 shadow-sm ${toneStyles}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">{value}</p>
          {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
        </div>
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${iconStyles}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

const parseCollectedAmount = (value: string) => {
  const amount = Number(String(value).replace(/[^\d.-]/g, ''));
  return Number.isFinite(amount) ? amount : 0;
};

export default function PaymentManagement() {
  const { isAuthenticated } = useAppContext();
  // Subscribe here so leaving/returning to this tab refetches in the background.
  usePaymentsManagementQuery(isAuthenticated);

  const {
    management,
    isManagementLoading: isLoading,
    isManagementFetching,
    managementError: error,
    getManagementByMemberId,
  } = usePaymentContext();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addOpenedFromView, setAddOpenedFromView] = useState(false);
  const [sort, setSort] = useState<ColumnSort | null>({ field: 'id', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const memberRows = useMemo(
    () =>
      (management?.data ?? []).map((item) => PaymentManagementAPIResponseMapper(item)),
    [management]
  );

  const overview = useMemo(
    () => ({
      totalMembers: management?.total_members ?? 0,
      paidThisMonth: management?.paid_this_month ?? 0,
      unpaidThisMonth: management?.unpaid_this_month ?? 0,
      totalCollected: parseCollectedAmount(management?.total_collected ?? '0'),
    }),
    [management]
  );

  const filteredRows = useMemo(() => {
    const lowerSearchTerm = searchTerm.toLowerCase();

    return memberRows.filter((row) => {
      const matchesSearch =
        row.member.id.includes(searchTerm) ||
        row.member.name.toLowerCase().includes(lowerSearchTerm) ||
        row.member.phone.includes(searchTerm);

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'paid' &&
          getMembershipStatus(row.member, row.summary.monthsPaid) === 'active' &&
          row.isPaidThisMonth) ||
        (statusFilter === 'unpaid' &&
          getMembershipStatus(row.member, row.summary.monthsPaid) === 'active' &&
          !row.isPaidThisMonth);

      return matchesSearch && matchesStatus;
    });
  }, [memberRows, searchTerm, statusFilter]);

  const sortedRows = useMemo(() => {
    if (!sort) return filteredRows;

    const sorted = [...filteredRows].sort((a, b) => {
      let comparison = 0;

      switch (sort.field) {
        case 'id':
          comparison = a.member.id.localeCompare(b.member.id, undefined, {
            numeric: true,
            sensitivity: 'base',
          });
          break;
        case 'name':
          comparison = a.member.name.localeCompare(b.member.name);
          break;
        case 'totalPaid':
          comparison = a.summary.totalPaid - b.summary.totalPaid;
          break;
        case 'amountDue':
          comparison = a.summary.amountDue - b.summary.amountDue;
          break;
        case 'monthsPaid':
          comparison = a.summary.monthsPaid - b.summary.monthsPaid;
          break;
        case 'lastPaymentDate': {
          const aTime = a.lastPaymentDate ? new Date(a.lastPaymentDate).getTime() : 0;
          const bTime = b.lastPaymentDate ? new Date(b.lastPaymentDate).getTime() : 0;
          comparison = aTime - bTime;
          break;
        }
        case 'status':
          comparison = Number(a.isPaidThisMonth) - Number(b.isPaidThisMonth);
          break;
        default:
          return 0;
      }

      return sort.direction === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [filteredRows, sort]);

  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedRows.slice(startIndex, startIndex + pageSize);
  }, [sortedRows, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, sort]);

  const handleSort = useCallback((field: SortField) => {
    setSort((currentSort) => {
      if (currentSort?.field === field) {
        return {
          field,
          direction: currentSort.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { field, direction: 'asc' };
    });
  }, []);

  const handleView = useCallback((row: PaymentMemberRow) => {
    setSelectedMember(row.member);
    setIsViewOpen(true);
  }, []);

  const handleAddPayment = useCallback((row: PaymentMemberRow) => {
    if (isAddPaymentDisabled(row.member, row.summary.monthsPaid)) return;
    setSelectedMember(row.member);
    setIsViewOpen(false);
    setIsAddOpen(true);
  }, []);

  const handleAddFromView = useCallback((member: Member) => {
    const monthsPaid = getManagementByMemberId(member.id)?.paid_months ?? 0;
    if (isAddPaymentDisabled(member, monthsPaid)) return;
    setSelectedMember(member);
    setIsViewOpen(false);
    setAddOpenedFromView(true);
    setIsAddOpen(true);
  }, [getManagementByMemberId]);

  const exportPayments = useCallback(() => {
    const csvData = [
      ['Member ID', 'Name', 'Phone', 'This Month', 'Total Paid', 'Due', 'Months Paid', 'Last Payment'],
      ...sortedRows.map((row) => [
        row.member.id,
        row.member.name,
        row.member.phone,
        getStatusLabel(
          getPaymentMonthStatus(row.member, row.isPaidThisMonth, row.summary.monthsPaid)
        ),
        row.summary.totalPaid,
        row.summary.amountDue,
        row.summary.monthsPaid,
        row.lastPaymentDate ?? '',
      ]),
    ];

    const csvContent = csvData.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'payments_report.csv';
    anchor.click();
    window.URL.revokeObjectURL(url);
  }, [sortedRows]);

  const filterButtons: { id: StatusFilter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'paid', label: 'Paid this month' },
    { id: 'unpaid', label: 'Unpaid this month' },
  ];

  return (
    <div className="flex h-full flex-col p-5">
      <div className="mb-6 shrink-0">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-gray-900">Payment Management</h1>
              {isManagementFetching && !isLoading && (
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#4369a0]/25 border-t-[#4369a0]" />
              )}
            </div>
            <p className="mt-0.5 text-sm text-gray-500">
              Track member payments, dues, and collection status
            </p>
          </div>
        </div>
        <div className="h-px bg-gray-200/80" />
      </div>

      <div className="mb-5 grid shrink-0 grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          icon={Users}
          label="Total Members"
          value={isLoading ? '--' : String(overview.totalMembers)}
          hint="Registered in the system"
          tone="gray"
        />
        <SummaryCard
          icon={CheckCircle2}
          label="Paid This Month"
          value={isLoading ? '--' : String(overview.paidThisMonth)}
          hint={getCurrentMonthLabel()}
          tone="emerald"
        />
        <SummaryCard
          icon={AlertCircle}
          label="Unpaid This Month"
          value={isLoading ? '--' : String(overview.unpaidThisMonth)}
          hint="Needs follow-up"
          tone="amber"
        />
        <SummaryCard
          icon={IndianRupee}
          label="Total Collected"
          value={isLoading ? '--' : formatCurrency(overview.totalCollected)}
          hint="All time"
          tone="brand"
        />
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <Card className="flex min-h-0 flex-1 flex-col border-gray-200/80 shadow-sm">
        <CardContent className="flex min-h-0 flex-1 flex-col p-5">
          <div className="mb-4 flex shrink-0 flex-wrap items-center gap-3">
            <h2 className="whitespace-nowrap text-sm font-semibold text-gray-900">
              Member Payments
              <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                {sortedRows.length}
              </span>
            </h2>

            <div className="relative min-w-[220px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search by ID, name, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-9 border-gray-200 bg-white pl-9"
              />
            </div>

            <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-1">
              {filterButtons.map((filter) => (
                <Button
                  key={filter.id}
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setStatusFilter(filter.id)}
                  className={`h-8 px-3 text-xs ${
                    statusFilter === filter.id
                      ? 'bg-[#4369a0]/10 font-medium text-[#4369a0] hover:bg-[#4369a0]/15'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {filter.label}
                </Button>
              ))}
            </div>

            <Button
              variant="outline"
              onClick={exportPayments}
              className="h-9 whitespace-nowrap border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
            >
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </div>

          <div className="min-h-0 flex-1 overflow-hidden">
            {isLoading ? (
              <div className="flex h-full flex-col items-center justify-center rounded-xl border border-gray-200/80 bg-white">
                <span className="h-8 w-8 animate-spin rounded-full border-2 border-[#4369a0]/30 border-t-[#4369a0]" />
                <p className="mt-3 text-sm text-gray-500">Loading payments...</p>
              </div>
            ) : (
              <PaymentTable
                rows={paginatedRows}
                columns={createColumns(sort, handleSort, handleView, handleAddPayment)}
                sort={sort}
                onSort={handleSort}
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={sortedRows.length}
                onPageChange={setCurrentPage}
                onPageSizeChange={setPageSize}
              />
            )}
          </div>
        </CardContent>
      </Card>

      <ViewPayment
        member={selectedMember}
        open={isViewOpen}
        onOpenChange={setIsViewOpen}
        onAddPayment={handleAddFromView}
      />

      <UpsertPayment
        member={selectedMember}
        open={isAddOpen}
        onOpenChange={(open) => {
          setIsAddOpen(open);
          if (!open) setAddOpenedFromView(false);
        }}
        onBack={addOpenedFromView ? () => {
          setIsAddOpen(false);
          setAddOpenedFromView(false);
          setIsViewOpen(true);
        } : undefined}
        onSuccess={addOpenedFromView ? () => {
          setIsAddOpen(false);
          setAddOpenedFromView(false);
          setIsViewOpen(true);
        } : undefined}
      />
    </div>
  );
}
